// Alert correlation for the alarm console: machine alerts in, operational
// incidents out. Pure; checked by incidents.check.ts.
//
//   ONT incident   every active alert of one ONT, one row. Warning and
//                  critical on the same metric collapse to the worse one;
//                  an offline ONT absorbs its (now stale) optical alerts.
//   Group incident several ONT incidents that look like one cause:
//                  a PON outage, or a possible area power outage. Always
//                  worded as a possibility: the evidence is timing, not proof.
import type { Alert } from "./types";

export type Sev = "critical" | "warn" | "info";
export type Kind =
  | "power"        // dying-gasp: the ONT lost electrical power
  | "los"          // loss of signal: optical/fibre side
  | "offline"      // down, cause unknown
  | "optical"      // ONT receive power
  | "oltRx"        // OLT receive power from this ONT
  | "temperature"
  | "supply"       // voltage / current
  | "other";

const SEV_RANK: Record<Sev, number> = { critical: 0, warn: 1, info: 2 };
const sevOf = (s: string): Sev => (s === "critical" || s === "warn" ? s : "info");
export const worseSev = (a: Sev, b: Sev): Sev => (SEV_RANK[a] <= SEV_RANK[b] ? a : b);

/** Cause from a run-state alert message: "ONT X Offline (dying-gasp)". */
export function kindOf(a: Pick<Alert, "metric" | "message">): Kind {
  switch (a.metric) {
    case "ont.run_state": {
      const m = (a.message ?? "").toLowerCase();
      if (/dying[\s-]?gasp|power/.test(m)) return "power";
      if (/\blos\b|loss of signal|lofi|losi/.test(m)) return "los";
      return "offline";
    }
    case "ont.rx_power_dbm": return "optical";
    case "ont.olt_rx_dbm": return "oltRx";
    case "ont.temperature_c": return "temperature";
    case "ont.voltage_v": case "ont.current_ma": return "supply";
    default: return "other";
  }
}

const DOWN: Kind[] = ["power", "los", "offline"];
export const isDown = (k: Kind) => DOWN.includes(k);

/** A reading older than this is not "current": the incident says stale. */
export const STALE_MS = 15 * 60_000;

export type Incident = {
  key: string;
  kind: Kind;
  severity: Sev;
  /** Primary alert: the one whose value/threshold the row shows. */
  primary: Alert;
  alerts: Alert[];
  /** Kinds besides the primary (optical under an offline ONT, OLT RX with ONT RX). */
  related: Kind[];
  openedAt: string;          // earliest alert: how long it has been broken
  acked: boolean;            // every alert acknowledged
  ackedBy: string;
  owner: string;
  suppressed: boolean;
  stale: boolean;            // reading stopped arriving
  target: { label: string; place: string; ontId: number | null; ontNum: number | null; oltId: number | null; oltName: string; ponId: number | null; ponPath: string };
  /** Set on children of a group incident. */
  groupKey?: string;
};

export type Group = {
  key: string;
  kind: "ponOutage" | "powerOutage";
  severity: Sev;
  oltId: number | null; oltName: string; ponId: number | null; ponPath: string;
  children: Incident[];
  firstAt: string;
  spreadMs: number;          // first to last drop
  total: number | null;      // ONTs on that PON, when known
};

/** Minimum affected ONTs, and the window their drops must fall inside. */
export const GROUP_MIN = 3;
export const GROUP_WINDOW_MS = 5 * 60_000;

export function buildIncidents(alerts: Alert[], nowMs: number): Incident[] {
  const byTarget = new Map<string, Alert[]>();
  for (const a of alerts) {
    const k = `${a.target_type}:${a.target_id}`;
    byTarget.set(k, [...(byTarget.get(k) ?? []), a]);
  }
  const out: Incident[] = [];
  for (const [key, as] of byTarget) {
    // The primary: a down state beats any telemetry; then the worst severity;
    // then the newest (the escalation that is happening now).
    const ranked = [...as].sort((x, y) => {
      const dx = isDown(kindOf(x)) ? 0 : 1, dy = isDown(kindOf(y)) ? 0 : 1;
      return dx - dy || SEV_RANK[sevOf(x.severity)] - SEV_RANK[sevOf(y.severity)] || Date.parse(y.opened_at) - Date.parse(x.opened_at);
    });
    const primary = ranked[0];
    const kind = kindOf(primary);
    const severity = as.map((a) => sevOf(a.severity)).reduce(worseSev);
    const related = [...new Set(as.map(kindOf).filter((k) => k !== kind))];
    const openedAt = as.map((a) => a.opened_at).sort()[0];
    const ackedAll = as.every((a) => a.status === "ack");
    const withValue = as.filter((a) => a.value_at);
    // Telemetry that stopped: every measured alert's last reading is old.
    // A down ONT is not "stale" -- it has no reading by definition.
    const stale = !isDown(kind) && withValue.length > 0 && withValue.every((a) => nowMs - Date.parse(a.value_at!) > STALE_MS);
    out.push({
      key, kind, severity, primary, alerts: ranked, related, openedAt,
      acked: ackedAll,
      ackedBy: ranked.find((a) => a.acked_by_email)?.acked_by_email ?? "",
      owner: ranked.find((a) => a.assignee_email)?.assignee_email ?? "",
      suppressed: as.some((a) => a.suppressed_until && Date.parse(a.suppressed_until) > nowMs),
      stale,
      target: {
        label: primary.target_label ?? "", place: primary.target_place ?? "",
        ontId: primary.target_type === "ont" ? primary.target_id : null, ontNum: primary.ont_num ?? null,
        oltId: primary.olt_id ?? null, oltName: primary.olt_name ?? "", ponId: primary.pon_port_id ?? null, ponPath: primary.pon_path ?? "",
      },
    });
  }
  return out;
}

/**
 * Group ONT incidents that look like one cause. A PON outage: GROUP_MIN or
 * more ONTs on one PON went down inside GROUP_WINDOW_MS of each other. A
 * possible area power outage: the same, but with dying-gasp, across the OLT.
 * `ponTotals` (PON id -> ONT count) lets a whole dark PON say so.
 */
export function correlate(incidents: Incident[], ponTotals: Map<number, number> = new Map()): { groups: Group[]; rest: Incident[] } {
  const groups: Group[] = [];
  const taken = new Set<string>();
  const cluster = (members: Incident[]) => {
    const sorted = [...members].sort((a, b) => Date.parse(a.openedAt) - Date.parse(b.openedAt));
    // The largest run of drops that fits inside the window.
    let best: Incident[] = [];
    for (let i = 0; i < sorted.length; i++) {
      const run = sorted.filter((x, j) => j >= i && Date.parse(x.openedAt) - Date.parse(sorted[i].openedAt) <= GROUP_WINDOW_MS);
      if (run.length > best.length) best = run;
    }
    return best;
  };
  // Power first: many dying-gasps together are the stronger, clearer signal.
  const byOlt = new Map<number, Incident[]>();
  for (const i of incidents) if (i.kind === "power" && i.target.oltId !== null) byOlt.set(i.target.oltId, [...(byOlt.get(i.target.oltId) ?? []), i]);
  for (const [oltId, ms] of byOlt) {
    const run = cluster(ms);
    if (run.length < GROUP_MIN) continue;
    run.forEach((x) => taken.add(x.key));
    groups.push(mkGroup("powerOutage", run, { oltId, oltName: run[0].target.oltName, ponId: null, ponPath: "" }, null));
  }
  const byPon = new Map<number, Incident[]>();
  for (const i of incidents) if (isDown(i.kind) && !taken.has(i.key) && i.target.ponId !== null) byPon.set(i.target.ponId, [...(byPon.get(i.target.ponId) ?? []), i]);
  for (const [ponId, ms] of byPon) {
    const run = cluster(ms);
    if (run.length < GROUP_MIN) continue;
    run.forEach((x) => taken.add(x.key));
    const t = run[0].target;
    groups.push(mkGroup("ponOutage", run, { oltId: t.oltId, oltName: t.oltName, ponId, ponPath: t.ponPath }, ponTotals.get(ponId) ?? null));
  }
  const rest = incidents.filter((i) => !taken.has(i.key));
  return { groups, rest };
}

function mkGroup(kind: Group["kind"], run: Incident[], where: Pick<Group, "oltId" | "oltName" | "ponId" | "ponPath">, total: number | null): Group {
  const times = run.map((x) => Date.parse(x.openedAt));
  const key = `${kind}:${where.ponId ?? where.oltId}`;
  return {
    key, kind, ...where, total,
    severity: run.map((x) => x.severity).reduce(worseSev),
    children: run.map((x) => ({ ...x, groupKey: key })),
    firstAt: new Date(Math.min(...times)).toISOString(),
    spreadMs: Math.max(...times) - Math.min(...times),
  };
}

/**
 * Console order: unacknowledged critical, unacknowledged warning, then
 * acknowledged; suppressed last. Newest first inside a band: a new problem is
 * the one nobody is looking at yet.
 */
export function band(i: Pick<Incident, "severity" | "acked" | "suppressed">): number {
  if (i.suppressed) return 4;
  if (i.acked) return 3;
  return SEV_RANK[i.severity] + 0; // 0 critical, 1 warn, 2 info
}

export function sortIncidents<T extends Pick<Incident, "severity" | "acked" | "suppressed" | "openedAt">>(xs: T[]): T[] {
  return [...xs].sort((a, b) => band(a) - band(b) || Date.parse(b.openedAt) - Date.parse(a.openedAt));
}

export const groupAcked = (g: Group) => g.children.every((c) => c.acked);
